import React, { useState } from 'react';
import { Checkbox, MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { CheckmarkCircle16Regular, Copy16Regular, History16Regular, Sparkle16Regular, Warning16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideRowAction, GuideRowActions, GuideTable, GuideTh, guideCellClassName as cell, guideRowClassName } from '../../../theme/components/GuideTable';
import { GuidePagination } from '../../../theme/components/GuidePagination';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideBannerAction, GuideLinkButton } from '../../../theme/components/GuideButtons';
import { GuideTooltip } from '../../../theme/components/GuideTooltip';
import { StatusPill } from '../../../theme/components/StatusPill';
import { StatusTone } from '../../../theme/guideTokens';
import { formatImportAmount, formatImportDate, getApiErrorMessage, protocolLabel } from '../../../functions/ProviderConsumptionImport.Functions';
import {
  AssignmentSource,
  autoServiceChoice,
  DuplicateStatusIcon,
  duplicateStatusView,
  effectiveAssignment,
  effectiveService,
  EffectiveServiceOrigin,
  hasServiceChoice,
  isSelectable,
  ItemServiceOverride,
  pageSelectionState,
  sameServiceKeyFilters,
  sameSuggestionFilters,
  selectableIds,
  suggestionAssignment,
} from '../../../functions/ProviderConsumptionImport.Review.Functions';
import {
  ImportAssignment,
  ImportPreview,
  ImportPreviewItem,
  PREVIEW_ITEMS_PAGE_SIZES,
  ProtocolReferenceTemplate,
  ReferenceTemplatePart,
  SaveReferenceTemplateRequest,
  TemplateTestResult,
} from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import {
  ImportAssignmentBar,
  ImportItemErrorsNotice,
  ImportReviewFilters,
  ImportReviewSummary,
  ImportSelectionHeader,
  ImportServiceBar,
  ImportVoucherInfo,
} from './ImportItemsReview.Toolbar';
import { ImportAssignmentDialog } from './ImportAssignment.Dialog';
import { MedicalServiceChoiceDialog, MedicalServiceChoiceMode } from './MedicalServiceChoice.Dialog';
import { ProtocolReferenceTemplateEditor } from './ProtocolReferenceTemplateEditor';
import { ImportSelection } from './useImportSelection';
import { AssignmentOptions } from './useAssignmentOptions';
import './ProviderConsumptionImport.Styles.css';

export interface ImportItemsReviewProps {
  preview: ImportPreview;
  selection: ImportSelection;
  /** Importando: la tabla queda de sólo lectura. */
  busy?: boolean;
  /** "Ver archivo" del aviso de comprobante ya importado (ruta relativa al tenant). */
  onViewFile?: (path: string, name: string) => void;
  /** Protocolos y pacientes para asignar (US2); sin opciones disponibles no se ofrece asignar. */
  assignment?: AssignmentOptions;
  /** Formatos de referencia (US5): con ambos callbacks el menú de la fila ofrece "Definir formato a partir de este ítem". */
  templates?: ReviewTemplateCallbacks;
}

export interface ReviewTemplateCallbacks {
  /** POST .../protocol-reference-templates/test */
  onTest: (parts: ReferenceTemplatePart[], texts: string[]) => Promise<TemplateTestResult[]>;
  /** POST .../protocol-reference-templates (alta) */
  onCreate: (body: SaveReferenceTemplateRequest) => Promise<ProtocolReferenceTemplate | undefined>;
}

/** Sugerencias que invitan a definir un formato: ninguno coincidió o el proveedor no tiene (spec, US2 escenario 5). */
const DEFINE_TEMPLATE_STATUSES = ['NoTemplateMatch', 'NoTemplates'];

/** Texto del ítem donde el servidor busca la referencia: el nombre y, si no hay, la referencia de origen. */
const referenceTextOf = (item: ImportPreviewItem): string => item.name?.trim() || item.sourceReference?.trim() || '';

/** Ícono de Fluent por estado de duplicado (acompaña al texto; nunca sólo color). */
const STATUS_ICONS: Record<DuplicateStatusIcon, React.ReactElement> = {
  new: <Sparkle16Regular />,
  copy: <Copy16Regular />,
  possibleDuplicate: <Warning16Regular />,
  alreadyImported: <CheckmarkCircle16Regular />,
  importedOther: <History16Regular />,
};

/** Pastilla de la asignación (texto + símbolo; nunca sólo color). */
const ASSIGNMENT_PILLS: Record<Exclude<AssignmentSource, 'none'>, { tone: StatusTone; symbol: string }> = {
  general: { tone: 'neutral', symbol: '◇' },
  own: { tone: 'info', symbol: '●' },
  suggested: { tone: 'brand', symbol: '✦' },
  unassigned: { tone: 'neutral', symbol: '○' },
};

/** Chip de origen del servicio médico (texto + símbolo; nunca sólo color). */
const SERVICE_ORIGIN_PILLS: Record<Exclude<EffectiveServiceOrigin, 'Unknown'>, { tone: StatusTone; symbol: string }> = {
  Remembered: { tone: 'success', symbol: '●' },
  MatchedByCode: { tone: 'info', symbol: '✓' },
  MatchedByName: { tone: 'neutral', symbol: '≈' },
  WillCreate: { tone: 'warning', symbol: '+' },
  Chosen: { tone: 'brand', symbol: '■' },
  CreateOnRequest: { tone: 'brand', symbol: '+' },
};

/** Diálogo de servicio abierto: para la barra (general) o para una fila. */
type ServiceTarget = { scope: 'general'; mode: MedicalServiceChoiceMode } | { scope: 'item'; mode: MedicalServiceChoiceMode; item: ImportPreviewItem };

/** Moneda del importe seleccionado: la del comprobante, la de la página o la por defecto del mapeo. */
const currencyOf = (preview: ImportPreview, items: ImportPreviewItem[]): string | undefined =>
  preview.voucher?.currencyCode || items.find((item) => item.currencyCode)?.currencyCode || preview.normalization?.defaultCurrencyCode || undefined;

/**
 * Paso "Revisar ítems" del asistente (spec 009): todos los ítems de la vista previa congelada en una `GuideTable`
 * paginada en el servidor, con casilla por fila, selección por página / todos los filtrados / ninguno y resumen en vivo
 * (US1), estado de duplicado (US3) y asignación de protocolo y paciente general, por fila o desde la sugerencia (US2).
 * Componente controlado: el estado vive en `useImportSelection` (en el asistente). Los textos del archivo se muestran como
 * texto (React escapa; nunca HTML).
 */
export const ImportItemsReview = ({ preview, selection, busy, onViewFile, assignment, templates }: ImportItemsReviewProps) => {
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.review.${key}`, options) as string;
  const tra = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.review.assignment.${key}`, options) as string;
  const trs = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.review.suggestion.${key}`, options) as string;
  const trv = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.review.service.${key}`, options) as string;
  const items = selection.pageItems;
  const { state, dispatch } = selection;
  const filtered = state.filters.statuses.length > 0 || !!state.filters.search.trim();
  const pageState = pageSelectionState(items, state.selected);
  const readOnly = !!busy || !selection.ready;
  const canAssign = !!assignment?.available;

  // ---- asignación (US2) ----
  const [assignmentTarget, setAssignmentTarget] = useState<ImportPreviewItem | undefined>(undefined);
  const [applyingFor, setApplyingFor] = useState<string | undefined>(undefined);
  /** Resultado de "Aplicar a todos los iguales" / "misma clave" (región aria-live). */
  const [statusMessage, setStatusMessage] = useState<string | undefined>(undefined);
  const [applyError, setApplyError] = useState<unknown>(undefined);

  // ---- servicio médico (US6) ----
  const [serviceTarget, setServiceTarget] = useState<ServiceTarget | undefined>(undefined);

  // ---- formato de referencia (US5) ----
  const [templateItem, setTemplateItem] = useState<ImportPreviewItem | undefined>(undefined);

  const togglePage = () => (pageState === true ? selection.deselectPage() : selection.selectPage());

  /** "id - nombre" del protocolo de la lista; si no está, el número del servidor (sugerencia) o "#id". */
  const protocolNameOf = (protocolId: number, item?: ImportPreviewItem): string => {
    const protocol = assignment?.protocols.find((entry) => entry.id === protocolId);
    if (protocol) return protocolLabel(protocol);
    if (item?.suggestion?.protocolId === protocolId && item.suggestion.protocolName) return item.suggestion.protocolName;
    return `#${protocolId}`;
  };

  const assignmentText = (value: ImportAssignment, item?: ImportPreviewItem): string => {
    const patient = value.patientNumberInProtocol?.trim();
    return patient
      ? tra('valueWithPatient', { protocol: protocolNameOf(value.protocolId, item), patient })
      : tra('valueNoPatient', { protocol: protocolNameOf(value.protocolId, item) });
  };

  const applyToSame = async (item: ImportPreviewItem) => {
    const filter = sameSuggestionFilters(item);
    const value = suggestionAssignment(item.suggestion);
    if (!item.id || !filter || !value) return;
    setApplyingFor(item.id);
    setApplyError(undefined);
    setStatusMessage(undefined);
    try {
      const ids = await selection.idsMatching(filter);
      if (!ids) return;
      const itemIds = ids.includes(item.id) ? ids : [...ids, item.id];
      dispatch({ type: 'setItemAssignments', itemIds, override: { kind: 'own', fromSuggestion: true, assignment: value } });
      setStatusMessage(trs('applied', { count: itemIds.length }));
    } catch (error: unknown) {
      setApplyError(error);
    } finally {
      setApplyingFor(undefined);
    }
  };

  const renderInitState = () => {
    if (selection.initError !== undefined) {
      return (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pci-review-init-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('initError')}</MessageBarTitle>
            {getApiErrorMessage(selection.initError)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={selection.retry}>{tr('retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      );
    }
    return null;
  };

  const renderSuggestion = (item: ImportPreviewItem, id: string) => {
    const suggestion = item.suggestion;
    if (!suggestion) return null;
    const extracted = suggestion.extracted?.protocol?.trim() || '—';
    if (suggestion.status === 'NotFound' || suggestion.status === 'Ambiguous') {
      return (
        <span className="pci-review-suggestion pci-review-suggestion--warning" data-testid={`pci-review-suggestion-${id}`}>
          <Warning16Regular aria-hidden />
          {suggestion.status === 'NotFound' ? trs('notFound', { protocol: extracted }) : trs('ambiguous', { protocol: extracted })}
        </span>
      );
    }
    const value = suggestionAssignment(suggestion);
    if (suggestion.status !== 'Matched' || !value) return null;
    const patient = suggestion.patientNumberInProtocol?.trim();
    // Número de protocolo del servidor ("BGB-11417-302", research R-03); si falta, el de la lista.
    const protocol = suggestion.protocolName?.trim() || protocolNameOf(value.protocolId, item);
    const label =
      patient && suggestion.patientValid !== false ? trs('matchedWithPatient', { protocol, patient }) : trs('matched', { protocol });
    const applying = applyingFor === item.id;
    return (
      <span className="pci-review-suggestion" data-testid={`pci-review-suggestion-${id}`}>
        <span>{label}</span>
        {patient && suggestion.patientValid === false && (
          <span className="pci-review-suggestion__warning" data-testid={`pci-review-suggestion-patient-${id}`}>
            <Warning16Regular aria-hidden />
            {trs('patientInvalid', { patient })}
          </span>
        )}
        <span className="pci-review-suggestion__actions">
          <GuideLinkButton
            size="small"
            disabled={readOnly || !item.id}
            onClick={() => item.id && dispatch({ type: 'applySuggestion', items: [item] })}
            aria-label={trs('applyLabel', { row: item.rowNumber })}
            data-testid={`pci-review-apply-${id}`}
          >
            {trs('apply')}
          </GuideLinkButton>
          <GuideLinkButton
            size="small"
            disabled={readOnly || !item.id || !!applyingFor}
            aria-busy={applying || undefined}
            onClick={() => applyToSame(item)}
            aria-label={trs('applyAllLabel', { protocol })}
            data-testid={`pci-review-apply-all-${id}`}
          >
            {applying ? trs('applying') : trs('applyAll')}
          </GuideLinkButton>
        </span>
      </span>
    );
  };

  const renderAssignment = (item: ImportPreviewItem, id: string) => {
    if (!item.id) return <span className="pci-secondary-cell">—</span>;
    const effective = effectiveAssignment(state, item.id);
    const showSuggestion = effective.source === 'none' || effective.source === 'general';
    return (
      <div className="pci-review-assignment" data-testid={`pci-review-assignment-${id}`} data-source={effective.source}>
        {effective.source === 'none' && <span className="pci-secondary-cell">{tra('none')}</span>}
        {effective.source === 'unassigned' && (
          <StatusPill {...ASSIGNMENT_PILLS.unassigned} className="pci-review-pill">
            {tra('sources.unassigned')}
          </StatusPill>
        )}
        {effective.assignment && effective.source !== 'unassigned' && effective.source !== 'none' && (
          <>
            <span className="pci-review-assignment__value">{assignmentText(effective.assignment, item)}</span>
            <StatusPill {...ASSIGNMENT_PILLS[effective.source]} className="pci-review-pill">
              {tra(`sources.${effective.source}`)}
            </StatusPill>
          </>
        )}
        {showSuggestion && renderSuggestion(item, id)}
      </div>
    );
  };

  /** Elección del diálogo de servicio: general, la fila o (con "misma clave") todos los ítems con la clave de la fila. */
  const confirmService = async (target: ServiceTarget, choice: ItemServiceOverride, applyToSameKey: boolean) => {
    setServiceTarget(undefined);
    if (target.scope === 'general') {
      dispatch({ type: 'setDefaultService', choice });
      return;
    }
    const item = target.item;
    if (!item.id) return;
    const filter = applyToSameKey ? sameServiceKeyFilters(item) : undefined;
    if (!filter) {
      dispatch({ type: 'setItemService', itemId: item.id, choice });
      return;
    }
    setApplyingFor(item.id);
    setApplyError(undefined);
    setStatusMessage(undefined);
    try {
      const ids = await selection.idsMatching(filter);
      if (!ids) return;
      const itemIds = ids.includes(item.id) ? ids : [...ids, item.id];
      dispatch({ type: 'setItemServices', itemIds, choice });
      setStatusMessage(trv('applied', { count: itemIds.length }));
    } catch (error: unknown) {
      dispatch({ type: 'setItemService', itemId: item.id, choice });
      setApplyError(error);
    } finally {
      setApplyingFor(undefined);
    }
  };

  const renderService = (item: ImportPreviewItem, id: string) => {
    const service = effectiveService(state, item);
    if (service.origin === 'Unknown') {
      return (
        <span className="pci-secondary-cell" data-testid={`pci-review-service-${id}`} data-origin="Unknown">
          —
        </span>
      );
    }
    const pill = SERVICE_ORIGIN_PILLS[service.origin];
    const name = service.name?.trim() || (service.medicalServiceId ? trv('serviceId', { id: service.medicalServiceId }) : '—');
    return (
      <div className="pci-review-service" data-testid={`pci-review-service-${id}`} data-origin={service.origin} data-source={service.source}>
        <span className="pci-review-service__name">{name}</span>
        {service.code && service.origin !== 'Chosen' && <span className="pci-secondary">{service.code}</span>}
        <span className="pci-review-service__chips">
          <StatusPill {...pill} className="pci-review-pill" data-testid={`pci-review-service-origin-${id}`}>
            {trv(`origins.${service.origin}`)}
          </StatusPill>
          {service.source === 'general' && <span className="pci-secondary-cell">{trv('fromGeneral')}</span>}
        </span>
        {service.origin === 'WillCreate' && service.variableDescriptionWarning && (
          <span className="pci-review-suggestion__warning" data-testid={`pci-review-service-variable-${id}`}>
            <Warning16Regular aria-hidden />
            {trv('variableDescription')}
          </span>
        )}
        {service.serviceInactive && (
          <StatusPill
            tone="warning"
            symbol="!"
            icon={<Warning16Regular />}
            className="pci-review-pill pci-review-pill--wrap"
            data-testid={`pci-review-service-inactive-${id}`}
          >
            {trv('inactive', { service: service.name?.trim() || name })}
          </StatusPill>
        )}
      </div>
    );
  };

  const rowActions = (item: ImportPreviewItem): GuideRowAction[] => {
    if (!item.id) return [];
    const itemId = item.id;
    const override = state.itemAssignments.get(itemId);
    const actions: GuideRowAction[] = [];
    if (canAssign) {
      actions.push({ key: 'change', label: tra('change'), onClick: () => setAssignmentTarget(item), disabled: readOnly });
      actions.push({
        key: 'unassign',
        label: tra('unassign'),
        onClick: () => dispatch({ type: 'setItemAssignment', itemId, override: { kind: 'unassigned' } }),
        disabled: readOnly || override?.kind === 'unassigned',
      });
      if (override) {
        actions.push({ key: 'useGeneral', label: tra('useGeneral'), onClick: () => dispatch({ type: 'setItemAssignment', itemId }), disabled: readOnly });
      }
    }
    actions.push({ key: 'service', label: trv('assign'), onClick: () => setServiceTarget({ scope: 'item', mode: 'pick', item }), disabled: readOnly });
    actions.push({ key: 'createService', label: trv('createNew'), onClick: () => setServiceTarget({ scope: 'item', mode: 'create', item }), disabled: readOnly });
    if (hasServiceChoice(state, itemId)) {
      actions.push({
        key: 'autoService',
        label: trv('backToAuto'),
        onClick: () => dispatch({ type: 'setItemService', itemId, choice: autoServiceChoice(state) }),
        disabled: readOnly,
      });
    }
    if (templates && referenceTextOf(item) && (!item.suggestion || DEFINE_TEMPLATE_STATUSES.includes(item.suggestion.status))) {
      actions.push({
        key: 'defineTemplate',
        label: t('providerConsumptionImport.templates.defineFromItem') as string,
        onClick: () => setTemplateItem(item),
        disabled: readOnly,
      });
    }
    return actions;
  };

  const renderRow = (item: ImportPreviewItem, index: number) => {
    const id = item.id ?? `row-${item.rowNumber}-${index}`;
    const selectable = !!item.id && isSelectable(item);
    const checked = !!item.id && state.selected.has(item.id);
    const errors = item.id ? state.itemErrors.get(item.id) ?? [] : [];
    const warnings = item.warnings ?? [];
    const reasonId = `pci-review-reason-${id}`;
    const status = duplicateStatusView(item, (key, options) => t(key, options) as string);
    const actions = rowActions(item);
    const checkbox = (
      <Checkbox
        checked={checked}
        disabled={!selectable || readOnly}
        onChange={() => item.id && selection.toggle(item.id)}
        aria-label={tr('selectRow', { row: item.rowNumber })}
        aria-describedby={!selectable ? reasonId : undefined}
        data-testid={`pci-review-check-${id}`}
      />
    );
    return (
      <tr
        key={id}
        className={guideRowClassName({ selected: checked, pending: errors.length > 0, inactive: !selectable })}
        data-testid={`pci-review-row-${id}`}
      >
        <td className={cell('check')}>
          {selectable ? (
            checkbox
          ) : (
            <GuideTooltip content={tr('notSelectable')} relationship="description">
              <span className="pci-review-check-disabled" tabIndex={0}>
                {checkbox}
                <span id={reasonId} className="pci-sr">
                  {tr('notSelectable')}
                </span>
              </span>
            </GuideTooltip>
          )}
        </td>
        <td className="pci-nowrap">
          <span className="ichtys-num">{item.rowNumber}</span>
          {(item.sourceReference || item.page) && (
            <span className="pci-secondary">{item.sourceReference || tr('pageOf', { page: item.page })}</span>
          )}
        </td>
        <td className="ichtys-num">{item.code || '—'}</td>
        <td>
          <span className="pci-review-name">{item.name || '—'}</span>
          {errors.map((error, errorIndex) => (
            <span key={errorIndex} className="pci-review-row-error" data-testid={`pci-review-row-error-${id}`}>
              {tra('rowError', { field: tra(`errorFields.${error.field}`), message: error.message })}
            </span>
          ))}
        </td>
        <td className="ichtys-num pci-nowrap">{formatImportDate(item.serviceDate)}</td>
        <td className={cell('end', 'num')}>{item.quantity ?? 1}</td>
        <td className={cell('end', 'num')}>
          <span className="pci-nowrap">{formatImportAmount(item.amount, item.currencyCode)}</span>
        </td>
        <td>
          <StatusPill
            tone={status.tone}
            symbol={status.symbol}
            icon={STATUS_ICONS[status.icon]}
            className="pci-review-status"
            data-status={status.status}
            data-testid={`pci-review-status-${id}`}
          >
            {status.label}
          </StatusPill>
          {status.detail && <span className="pci-secondary">{status.detail}</span>}
        </td>
        <td>{renderAssignment(item, id)}</td>
        <td>{renderService(item, id)}</td>
        <td>{item.patientReference || <span className="pci-secondary-cell">—</span>}</td>
        <td>
          {warnings.length === 0 ? (
            <span className="pci-secondary-cell">—</span>
          ) : (
            <ul className="pci-list pci-list--compact">
              {warnings.map((warning, warningIndex) => (
                <li key={warningIndex}>{warning}</li>
              ))}
            </ul>
          )}
        </td>
        <td className={cell('actions')} data-testid={`pci-review-actions-${id}`}>
          {actions.length > 0 && <GuideRowActions label={tr('rowActions', { row: item.rowNumber })} actions={actions} disabled={readOnly} />}
        </td>
      </tr>
    );
  };

  const renderTable = () => {
    if (selection.pageError !== undefined && items.length === 0) {
      return (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pci-review-page-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadError')}</MessageBarTitle>
            {getApiErrorMessage(selection.pageError)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={selection.retry}>{tr('retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      );
    }
    if (selection.pageLoading && items.length === 0) return <GuideSkeleton rows={5} label={tr('loading')} data-testid="pci-review-loading" />;
    if (!selection.pageLoading && items.length === 0 && selection.totalRecords === 0) {
      return filtered ? (
        <GuideEmptyState title={tr('noMatchTitle')} text={tr('noMatchText')} data-testid="pci-review-no-match" />
      ) : (
        <GuideEmptyState title={tr('emptyTitle')} text={tr('emptyText')} data-testid="pci-review-empty" />
      );
    }
    return (
      <>
        {selection.pageError !== undefined && (
          <GuideMessageBar intent="error" layout="multiline" data-testid="pci-review-page-error">
            <MessageBarBody>
              <MessageBarTitle>{tr('loadError')}</MessageBarTitle>
              {getApiErrorMessage(selection.pageError)}
            </MessageBarBody>
            <MessageBarActions>
              <GuideBannerAction onClick={selection.retry}>{tr('retry')}</GuideBannerAction>
            </MessageBarActions>
          </GuideMessageBar>
        )}
        <GuideTable
          ariaLabel={tr('tableLabel')}
          data-testid="pci-review-table"
          footer={
            <GuidePagination
              page={state.page.pageNumber}
              pageSize={state.page.pageSize}
              total={selection.totalRecords}
              pageSizeOptions={PREVIEW_ITEMS_PAGE_SIZES}
              onPageChange={selection.setPage}
              onPageSizeChange={selection.setPageSize}
            />
          }
        >
          <thead>
            <tr>
              <th className={cell('check')} scope="col">
                <ImportSelectionHeader
                  checked={pageState}
                  disabled={selectableIds(items).length === 0 || readOnly}
                  busy={selection.selectingAll}
                  onTogglePage={togglePage}
                  onSelectPage={selection.selectPage}
                  onSelectAllFiltered={selection.selectAllFiltered}
                  onSelectNone={selection.selectNone}
                />
              </th>
              <GuideTh>{tr('columns.row')}</GuideTh>
              <GuideTh>{tr('columns.code')}</GuideTh>
              <GuideTh>{tr('columns.name')}</GuideTh>
              <GuideTh>{tr('columns.date')}</GuideTh>
              <GuideTh align="end">{tr('columns.quantity')}</GuideTh>
              <GuideTh align="end">{tr('columns.amount')}</GuideTh>
              <GuideTh>{tr('columns.status')}</GuideTh>
              <GuideTh>{tr('columns.assignment')}</GuideTh>
              <GuideTh>{tr('columns.service')}</GuideTh>
              <GuideTh>{tr('columns.patient')}</GuideTh>
              <GuideTh>{tr('columns.warnings')}</GuideTh>
              <th className={cell('actions')} scope="col">
                <span className="pci-sr">{tr('columns.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody aria-busy={selection.pageLoading || undefined}>{items.map(renderRow)}</tbody>
        </GuideTable>
      </>
    );
  };

  const target = assignmentTarget;
  const targetInitial = target?.id ? effectiveAssignment(state, target.id).assignment : undefined;
  const serviceItem = serviceTarget?.scope === 'item' ? serviceTarget.item : undefined;
  const serviceInitialId = serviceItem ? effectiveService(state, serviceItem).medicalServiceId : undefined;
  const sameKey = serviceItem ? sameServiceKeyFilters(serviceItem)?.serviceSourceKey : undefined;

  return (
    <div className="pci-stack" data-testid="pci-review">
      <GuideDrawerSection title={tr('title')} description={tr('description')} testId="pci-review-section">
        <ImportVoucherInfo voucher={preview.voucher} voucherAlreadyImported={preview.voucherAlreadyImported} onViewFile={onViewFile} />
        <ImportReviewSummary
          detected={Number(preview.summary?.total ?? selection.totalRecords)}
          selected={selection.count}
          amount={selection.amount}
          currencyCode={currencyOf(preview, items)}
        />
        {canAssign && assignment && (
          <ImportAssignmentBar
            options={assignment}
            value={state.defaultAssignment}
            onChange={(value) => dispatch({ type: 'setDefaultAssignment', assignment: value })}
            selectedCount={selection.count}
            disabled={readOnly}
          />
        )}
        <ImportServiceBar
          value={state.defaultService}
          remember={state.remember}
          onRememberChange={(value) => dispatch({ type: 'setRemember', value })}
          onAssign={() => setServiceTarget({ scope: 'general', mode: 'pick' })}
          onCreate={() => setServiceTarget({ scope: 'general', mode: 'create' })}
          onClear={() => dispatch({ type: 'setDefaultService', choice: undefined })}
          selectedCount={selection.count}
          disabled={readOnly}
        />
        <ImportReviewFilters
          summary={preview.summary}
          statuses={state.filters.statuses}
          search={state.filters.search}
          onStatusesChange={selection.setStatuses}
          onSearchChange={selection.setSearch}
          disabled={!!busy}
        />
        {renderInitState()}
        <ImportItemErrorsNotice itemErrors={state.itemErrors} />
        {selection.selectAllError !== undefined && (
          <GuideMessageBar intent="error" layout="multiline" data-testid="pci-review-select-all-error">
            <MessageBarBody>
              <MessageBarTitle>{tr('selectAllError')}</MessageBarTitle>
              {getApiErrorMessage(selection.selectAllError)}
            </MessageBarBody>
          </GuideMessageBar>
        )}
        {applyError !== undefined && (
          <GuideMessageBar intent="error" layout="multiline" data-testid="pci-review-apply-error">
            <MessageBarBody>
              <MessageBarTitle>{trs('applyError')}</MessageBarTitle>
              {getApiErrorMessage(applyError)}
            </MessageBarBody>
          </GuideMessageBar>
        )}
        <div className="pci-review-apply-status" aria-live="polite" data-testid="pci-review-apply-status">
          {statusMessage ?? ''}
        </div>
        {renderTable()}
      </GuideDrawerSection>
      <MedicalServiceChoiceDialog
        open={!!serviceTarget}
        mode={serviceTarget?.mode ?? 'pick'}
        title={serviceItem ? trv('dialog.titleRow', { row: serviceItem.rowNumber }) : trv('dialog.titleGeneral', { count: selection.count })}
        description={serviceItem?.name}
        initialCreate={serviceItem ? { code: serviceItem.code, name: serviceItem.name } : undefined}
        initialServiceId={serviceInitialId ?? undefined}
        sameKey={sameKey}
        onDismiss={() => setServiceTarget(undefined)}
        onConfirm={(choice, options) => serviceTarget && confirmService(serviceTarget, choice, options.applyToSameKey)}
      />
      {templates && (
        <ProtocolReferenceTemplateEditor
          open={!!templateItem}
          sampleText={templateItem ? referenceTextOf(templateItem) : undefined}
          context={templateItem ? (t('providerConsumptionImport.templates.rowContext', { row: templateItem.rowNumber }) as string) : undefined}
          testTexts={items.map(referenceTextOf)}
          onTest={templates.onTest}
          onSave={(body) => templates.onCreate(body)}
          onDismiss={() => setTemplateItem(undefined)}
          onSaved={(_template, name) => {
            setTemplateItem(undefined);
            // Las sugerencias se calculan al leer (MP DEC-014): se vuelve a pedir la página.
            selection.reloadPage();
            setStatusMessage(t('providerConsumptionImport.templates.savedFromItem', { name }) as string);
          }}
        />
      )}
      {assignment && (
        <ImportAssignmentDialog
          open={!!target}
          rowNumber={target?.rowNumber}
          itemName={target?.name}
          initial={targetInitial}
          options={assignment}
          onDismiss={() => setAssignmentTarget(undefined)}
          onSave={(value) => {
            if (target?.id) dispatch({ type: 'setItemAssignment', itemId: target.id, override: { kind: 'own', assignment: value } });
            setAssignmentTarget(undefined);
          }}
        />
      )}
    </div>
  );
};

export default ImportItemsReview;
